"use client";

import type {SelectProps as RACSelectProps} from "react-aria-components";
import {Select as RACSelect, SelectValue, Button} from "react-aria-components";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faCaretDown} from "@fortawesome/free-solid-svg-icons/faCaretDown";
import {faCaretUp} from "@fortawesome/free-solid-svg-icons/faCaretUp";
import clsMerge from "../../util/clsMerge";
import Label, {LabelClasses} from "./Label";
import FormHelperText, {HelperTextClasses} from "./FormHelperText";
import {SelectionList, SelectionListClasses} from "./SelectionList";

export type SelectClasses = {
	root?: string;
	label?: LabelClasses;
	inputRoot?: string;
	input?: string;
	selectionList?: SelectionListClasses;
	helperText?: HelperTextClasses;
};

// TODO: Enforce either labelText or ariaLabel is used
interface SelectProps<T extends object>
	extends Omit<RACSelectProps<T>, "children"> {
	children?: React.ReactNode | ((item: T) => React.ReactNode);
	classes?: SelectClasses;
	hideErrors?: boolean;
	hideLabelDecorators?: boolean;
	helperText?: string;
	items?: Iterable<T>;
	labelText?: string;
	ariaLabel?: string;
	shouldFlip?: boolean;
}

// https://react-spectrum.adobe.com/react-aria/Select.html
// TODO: figure out ref forwarding for generic type components
function Select<T extends object>({
	children,
	classes,
	helperText,
	hideErrors,
	hideLabelDecorators,
	items,
	labelText,
	ariaLabel,
	shouldFlip = true,
	...props
}: SelectProps<T>) {
	return (
		<RACSelect
			className={clsMerge("flex flex-col gap-1", classes?.root)}
			{...props}
		>
			{({isDisabled, isInvalid, isOpen}) => {
				return (
					<>
						{labelText && (
							<Label
								classes={classes?.label}
								isRequired={props.isRequired}
								isDisabled={isDisabled}
								hideDecorators={hideLabelDecorators}
							>
								{labelText}
							</Label>
						)}
						<Button
							className={clsMerge(
								"input focus-visible:input-focus flex flex-row items-center gap-2 bg-white p-4",
								isOpen && "input-focus",
								isDisabled && "input-disabled",
								isInvalid && "input-error",
								classes?.inputRoot
							)}
							aria-label={ariaLabel}
						>
							<SelectValue
								className={clsMerge(
									"flex-grow overflow-x-hidden overflow-ellipsis text-nowrap text-left",
									classes?.input
								)}
							/>
							<FontAwesomeIcon
								className="flex-0"
								icon={isOpen ? faCaretUp : faCaretDown}
							/>
						</Button>
						<FormHelperText
							classes={classes?.helperText}
							helperText={helperText}
							hideErrors={hideErrors}
						/>
						<SelectionList
							classes={classes?.selectionList}
							items={items}
							popoverProps={{offset: 4, shouldFlip}}
						>
							{children}
						</SelectionList>
					</>
				);
			}}
		</RACSelect>
	);
}

export default Select;
